/**
 * FINAL ACTIVATION DETAIL PAGE
 *
 * Final review and activation page for an organization's FINAL_ACTIVATION task.
 * Platform Team can review all locations and activate the entire organization or individual locations.
 * 
 * Supports:
 * - Full organization activation (all locations at once)
 * - Partial activation (individual locations) for early access
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { createClient } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/status-badge';
import { Building2, MapPin, CheckCircle, Clock, Rocket, Loader2 } from 'lucide-react';
import { processActivation } from '@/lib/onboarding-status';
import type { Task, Organization } from '@/types/database';

interface TaskWithRelations extends Task {
  organization: Pick<Organization, 'id' | 'name' | 'status' | 'logo_url'> | null;
}

interface LocationWithTasks {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  status: string | null;
  mapping_status: string | null;
  tasks: Array<{ id: string; type: string; status: string }>;
}

interface LocationStatus {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  status: string;
  mapping_status: string | null;
  itComplete: boolean;
  dataComplete: boolean;
  readyForActivation: boolean;
  isActivated: boolean;
}

function ActivationDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [task, setTask] = useState<TaskWithRelations | null>(null);
  const [locationStatuses, setLocationStatuses] = useState<LocationStatus[]>([]);
  const [, setAllLocationsComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [activatingLocationId, setActivatingLocationId] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState<any>(null);

  // Auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/auth/signin');
    return null;
  }

  // Data fetching
  useEffect(() => {
    const fetchTaskData = async () => {
      if (!taskId) {
        navigate('/activation');
        return;
      }

      try {
        setLoading(true);
        const supabase = createClient();

        // Fetch FINAL_ACTIVATION task at organization level
        const { data: taskData, error: taskError } = await supabase
          .from('tasks')
          .select(
            `
            *,
            organization:organizations(id, name, status, logo_url)
          `
          )
          .eq('id', taskId)
          .eq('type', 'FINAL_ACTIVATION')
          .is('location_id', null)
          .single();

        if (taskError) {
          console.error('Error fetching task:', taskError);
          navigate('/activation');
          return;
        }

        if (!taskData || !taskData.organization) {
          navigate('/activation');
          return;
        }

        setTask(taskData as TaskWithRelations);

        // Fetch onboarding_queue data separately using organization ID
        if (taskData.organization_id) {
          const { data: queueData } = await supabase
            .from('onboarding_queue')
            .select('primary_contact_name, primary_contact_email, phone_number')
            .eq('id', taskData.organization_id)
            .maybeSingle();

          if (queueData) {
            setContactInfo(queueData);
          }
        }

        // Fetch all locations for this organization with their task statuses
        const { data: locations, error: locError } = await supabase
          .from('locations')
          .select(
            `
            id,
            name,
            description,
            logo_url,
            status,
            mapping_status,
            tasks:tasks(id, type, status)
          `
          )
          .eq('organization_id', taskData.organization_id)
          .order('created_at');

        if (locError) {
          console.error('Error fetching locations:', locError);
        }

        // Check if all locations have completed both IT and Data tasks
        let allComplete = true;
        const statuses: LocationStatus[] = (locations || []).map((loc: LocationWithTasks) => {
          const itTask = loc.tasks?.find((t) => t.type === 'IT_PROVISIONING');
          const dataTask = loc.tasks?.find((t) => t.type === 'DATA_CONFIGURATION');

          const itComplete = itTask?.status === 'COMPLETED';
          const dataComplete = dataTask?.status === 'COMPLETED';
          const readyForActivation = itComplete && dataComplete;
          const isActivated = loc.status === 'ACTIVE';

          if (!readyForActivation) allComplete = false;

          return {
            id: loc.id,
            name: loc.name,
            description: loc.description,
            logo_url: loc.logo_url,
            status: loc.status || '',
            mapping_status: loc.mapping_status,
            itComplete,
            dataComplete,
            readyForActivation,
            isActivated,
          };
        });

        setLocationStatuses(statuses);
        setAllLocationsComplete(allComplete);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTaskData();
  }, [taskId, navigate]);

  // Handler for activating individual location
  const handleActivateLocation = async (locationId: string) => {
    if (!task?.organization) return;

    try {
      setActivatingLocationId(locationId);
      const supabase = createClient();

      // Process activation via edge function (handles logic, updates, and logging)
      await processActivation(task.organization.id, locationId, user?.id);

      // Refresh location statuses locally to reflect changes
      const { data: locations } = await supabase
        .from('locations')
        .select(
          `
            id,
            name,
            description,
            logo_url,
            status,
            mapping_status,
            tasks:tasks(id, type, status)
          `
        )
        .eq('organization_id', task.organization_id)
        .order('created_at');

      if (locations) {
        const statuses: LocationStatus[] = (locations || []).map((loc: LocationWithTasks) => {
          const itTask = loc.tasks?.find((t) => t.type === 'IT_PROVISIONING');
          const dataTask = loc.tasks?.find((t) => t.type === 'DATA_CONFIGURATION');

          const itComplete = itTask?.status === 'COMPLETED';
          const dataComplete = dataTask?.status === 'COMPLETED';
          const readyForActivation = itComplete && dataComplete;
          const isActivated = loc.status === 'ACTIVE';

          return {
            id: loc.id,
            name: loc.name,
            description: loc.description,
            logo_url: loc.logo_url,
            status: loc.status || '',
            mapping_status: loc.mapping_status,
            itComplete,
            dataComplete,
            readyForActivation,
            isActivated,
          };
        });

        setLocationStatuses(statuses);
        
        // If all are activated, the edge function will have marked the task as completed.
        // We can just refresh or wait for the user to navigate away. 
        // For good UX, we can check if the task is now complete.
        if (statuses.every(loc => loc.isActivated)) {
           console.log('All locations activated, refreshing UI...');
           setTimeout(() => {
              navigate('/activation');
           }, 1500);
        }
      }
    } catch (err) {
      console.error('Error activating location:', err);
      alert(`Failed to activate location: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActivatingLocationId(null);
    }
  };

  // Render states
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading task...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">Error loading task</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  const isActivated = task.status === 'COMPLETED';
  // const canActivate = allLocationsComplete && !isActivated;

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header - Focus on Organization */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          {/* Organization Logo */}
          {task.organization?.logo_url ? (
            <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800">
              <img
                src={task.organization.logo_url}
                alt={`${task.organization?.name} logo`}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
              <Building2 className="w-10 h-10 text-gray-400" />
            </div>
          )}

          {/* Header Content */}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {task.organization?.name}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {locationStatuses.length} location{locationStatuses.length !== 1 ? 's' : ''}
            </p>
            <div className="mt-3">
              <StatusBadge status={task.status} />
            </div>
          </div>
        </div>
      </div>

      {/* Organization Details */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organization Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Name</p>
              <p className="font-medium">{task.organization?.name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Status</p>
              <p className="font-medium">
                <StatusBadge status={task.organization?.status || 'PENDING'} />
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Primary Contact Information */}
      {contactInfo && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Primary Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {contactInfo.primary_contact_name && (
                <div>
                  <p className="text-xs text-muted-foreground">Contact Name</p>
                  <p className="font-medium text-sm">{contactInfo.primary_contact_name}</p>
                </div>
              )}
              {contactInfo.primary_contact_email && (
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="font-medium text-sm">{contactInfo.primary_contact_email}</p>
                </div>
              )}
              {contactInfo.phone_number && (
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-medium text-sm">{contactInfo.phone_number}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Locations Overview */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location Statuses
          </CardTitle>
          <CardDescription>
            Locations can be activated individually when ready, or all at once when all are complete
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {locationStatuses.map((loc) => (
              <div
                key={loc.id}
                className={`p-4 rounded-lg border ${
                  loc.isActivated
                    ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                    : loc.readyForActivation
                    ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
                    : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-sm">{loc.name}</p>
                      {loc.isActivated && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          <CheckCircle className="h-3 w-3" />
                          Active
                        </span>
                      )}
                    </div>
                    {loc.description && (
                      <p className="text-xs text-muted-foreground mt-1">{loc.description}</p>
                    )}
                  </div>
                  <div className="flex gap-6 items-center">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground mb-1">IT Setup</p>
                      <div className="flex items-center gap-1 justify-end">
                        {loc.itComplete ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-xs font-semibold text-green-600 dark:text-green-400">Done</span>
                          </>
                        ) : (
                          <>
                            <Clock className="h-4 w-4 text-amber-500" />
                            <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Pending</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground mb-1">Data Configuration</p>
                      <div className="flex items-center gap-1 justify-end">
                        {loc.dataComplete ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-xs font-semibold text-green-600 dark:text-green-400">Done</span>
                          </>
                        ) : (
                          <>
                            <Clock className="h-4 w-4 text-amber-500" />
                            <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Pending</span>
                          </>
                        )}
                      </div>
                    </div>
                    {!loc.isActivated && loc.readyForActivation && (
                      <div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleActivateLocation(loc.id)}
                          disabled={!!activatingLocationId}
                          className="text-xs"
                        >
                          {activatingLocationId === loc.id ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Activating...
                            </>
                          ) : (
                            <>
                              <Rocket className="h-3 w-3 mr-1" />
                              Activate
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Activation Status or Warning */}
      {isActivated && task.completed_at ? (
        <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 mb-6">
          <CardHeader>
            <CardTitle className="text-green-700 dark:text-green-400">All Locations Activated</CardTitle>
            <CardDescription>
              All locations for this organization have been successfully activated. Task completed on{' '}
              {new Date(task.completed_at).toLocaleDateString()}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : locationStatuses.some(loc => loc.isActivated) ? (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 mb-6">
          <CardHeader>
            <CardTitle className="text-blue-700 dark:text-blue-400">Activation In Progress</CardTitle>
            <CardDescription>
              {locationStatuses.filter(loc => loc.isActivated).length} of {locationStatuses.length} location(s) 
              activated. Continue activating remaining locations using the "Activate" button on each ready location.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 mb-6">
          <CardHeader>
            <CardTitle className="text-amber-700 dark:text-amber-400">Ready to Start Activation</CardTitle>
            <CardDescription>
              Activate locations individually as they become ready by clicking the "Activate" button on each location card.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <span className="text-amber-600 dark:text-amber-400">✓</span>
                Locations are ready when both IT and Data tasks are complete
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-600 dark:text-amber-400">✓</span>
                Activate locations one at a time using individual "Activate" buttons
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-600 dark:text-amber-400">✓</span>
                Task will auto-complete when all locations are activated
              </li>
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Bulk Activation Button - Hidden for now to avoid confusion */}
      {/* Users should activate locations individually using the per-location buttons above */}
    </div>
  );
}

export default ActivationDetailPage;
